import { type FormEvent, useState } from "react";
import { Alert, Button } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { FormInput, VerticalForm } from "../components/custom/components";
import AuthLayout from "../components/custom/pages/AuthLayout";
import { ToastTypes, useToast } from "../components/toast/ToastProvider";
import { errorHandler, sendOtp, userForgotPassword } from "../services/otp.service";
import {
  resolveOtpExpirySeconds,
  useOtpExpiryCountdown,
} from "../utils/otp";

const ForgotPassword = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const [step, setStep] = useState<"email" | "reset">("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const {
    isActive: otpTimerActive,
    formattedCountdown,
    startCountdown,
  } = useOtpExpiryCountdown();

  const handleSendOtp = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setInfo("");

    const trimmedEmail = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setError("Enter a valid admin email address");
      return;
    }

    setLoading(true);
    try {
      const result = await sendOtp({
        email: trimmedEmail,
        purpose: "admin_reset",
      });
      setInfo(result.message);
      setStep("reset");
      startCountdown(resolveOtpExpirySeconds(result.expiresInSeconds));
    } catch (err) {
      setError(errorHandler(err, "Could not send verification code"));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (otpTimerActive) return;
    setError("");
    setInfo("");
    setLoading(true);
    try {
      const result = await sendOtp({
        email: email.trim(),
        purpose: "admin_reset",
      });
      setInfo(result.message);
      startCountdown(resolveOtpExpirySeconds(result.expiresInSeconds));
    } catch (err) {
      setError(errorHandler(err, "Could not resend code"));
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e: FormEvent) => {
    e.preventDefault();
    setError("");

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    if (otp.replace(/\D/g, "").length !== 6) {
      setError("Enter the 6-digit verification code");
      return;
    }

    setLoading(true);
    try {
      const result = await userForgotPassword({
        email: email.trim(),
        otp: otp.replace(/\D/g, ""),
        newPassword,
      });
      toast(result.message, ToastTypes.SUCCESS);
      navigate("/login");
    } catch (err) {
      setError(errorHandler(err, "Could not reset password"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout helpText="Reset password for admin accounts only.">
      <p className="text-white-50 small mb-3">
        Forgot password is available only for users with the ADMIN role. Enter
        the email registered on the admin account.
      </p>
      {error && <Alert variant="danger">{error}</Alert>}
      {info && <Alert variant="info">{info}</Alert>}

      {step === "email" ? (
        <VerticalForm onSubmit={handleSendOtp}>
          <FormInput
            label="Admin email"
            type="email"
            name="email"
            value={email}
            placeholder="admin@example.com"
            onChange={(e) => setEmail(e.target.value)}
            containerClass="mb-3"
            className="custom-input"
            labelClassName="text-white"
            required
          />
          <div className="text-center d-grid">
            <Button
              variant="primary"
              type="submit"
              className="btn-lg p-2"
              disabled={loading}
            >
              {loading ? "Sending..." : "Send verification code"}
            </Button>
          </div>
        </VerticalForm>
      ) : (
        <VerticalForm onSubmit={handleReset}>
          <FormInput
            label="Verification code"
            type="text"
            name="otp"
            value={otp}
            placeholder="6-digit code"
            onChange={(e) => setOtp(e.target.value)}
            containerClass="mb-3"
            className="custom-input"
            labelClassName="text-white"
            maxLength={6}
          />
          <FormInput
            label="New password"
            type="password"
            name="newPassword"
            value={newPassword}
            placeholder="New password"
            onChange={(e) => setNewPassword(e.target.value)}
            containerClass="mb-3"
            className="custom-input"
            labelClassName="text-white"
          />
          <FormInput
            label="Confirm password"
            type="password"
            name="confirmPassword"
            value={confirmPassword}
            placeholder="Confirm password"
            onChange={(e) => setConfirmPassword(e.target.value)}
            containerClass="mb-3"
            className="custom-input"
            labelClassName="text-white"
          />
          <div className="d-flex justify-content-between align-items-center mb-3">
            <button
              type="button"
              className="btn btn-link text-primary-50 p-0"
              onClick={handleResend}
              disabled={loading || otpTimerActive}
            >
              {otpTimerActive
                ? `Resend in ${formattedCountdown}`
                : "Resend code"}
            </button>
            <button
              type="button"
              className="btn btn-link text-primary-50 p-0"
              onClick={() => setStep("email")}
            >
              Change email
            </button>
          </div>
          <div className="text-center d-grid">
            <Button
              variant="primary"
              type="submit"
              className="btn-lg p-2"
              disabled={loading}
            >
              {loading ? "Resetting..." : "Reset password"}
            </Button>
          </div>
        </VerticalForm>
      )}

      <div className="mt-3 text-center">
        <Link to="/login" className="text-primary-50">
          Back to login
        </Link>
      </div>
    </AuthLayout>
  );
};

export default ForgotPassword;
